import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Phone, Webhook, Database, Mail, Settings as SettingsIcon, Save } from 'lucide-react';
import { toast } from 'sonner';

const Settings = () => {
  const [configs, setConfigs] = useState({
    twilio: {
      account_sid: '',
      auth_token: '',
      phone_number: ''
    },
    make: {
      webhook_url: ''
    },
    fub: {
      api_key: '',
      base_url: 'https://api.followupboss.com/v1'
    },
    email: {
      smtp_host: 'smtp.gmail.com',
      smtp_port: '587',
      smtp_user: '',
      smtp_password: ''
    }
  });

  const handleSave = (section) => {
    toast.success(`${section} settings saved! Please update .env file on the server.`);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold text-slate-800 mb-2">Settings</h1>
        <p className="text-slate-600">Configure integrations and system preferences</p>
      </div>

      <Tabs defaultValue="twilio" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 lg:w-auto">
          <TabsTrigger value="twilio" data-testid="tab-twilio">
            <Phone className="w-4 h-4 mr-2" />
            Twilio
          </TabsTrigger>
          <TabsTrigger value="make" data-testid="tab-make">
            <Webhook className="w-4 h-4 mr-2" />
            Make.com
          </TabsTrigger>
          <TabsTrigger value="fub" data-testid="tab-fub">
            <Database className="w-4 h-4 mr-2" />
            FUB CRM
          </TabsTrigger>
          <TabsTrigger value="email" data-testid="tab-email">
            <Mail className="w-4 h-4 mr-2" />
            Email
          </TabsTrigger>
        </TabsList>

        {/* Twilio Settings */}
        <TabsContent value="twilio">
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-400 to-red-600 flex items-center justify-center">
                <Phone className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-800">Twilio Configuration</h3>
                <p className="text-sm text-slate-600">Voice and SMS integration</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Account SID</label>
                <Input
                  data-testid="twilio-sid-input"
                  value={configs.twilio.account_sid}
                  onChange={(e) =>
                    setConfigs({
                      ...configs,
                      twilio: { ...configs.twilio, account_sid: e.target.value }
                    })
                  }
                  placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Auth Token</label>
                <Input
                  data-testid="twilio-token-input"
                  type="password"
                  value={configs.twilio.auth_token}
                  onChange={(e) =>
                    setConfigs({
                      ...configs,
                      twilio: { ...configs.twilio, auth_token: e.target.value }
                    })
                  }
                  placeholder="Your Twilio Auth Token"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Phone Number</label>
                <Input
                  data-testid="twilio-phone-input"
                  value={configs.twilio.phone_number}
                  onChange={(e) =>
                    setConfigs({
                      ...configs,
                      twilio: { ...configs.twilio, phone_number: e.target.value }
                    })
                  }
                  placeholder="+1234567890"
                />
              </div>
              <Button
                data-testid="save-twilio-button"
                onClick={() => handleSave('Twilio')}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Twilio Settings
              </Button>
            </div>

            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800 font-medium mb-2">Getting Started:</p>
              <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                <li>Sign up at twilio.com</li>
                <li>Get your Account SID and Auth Token from the console</li>
                <li>Purchase a phone number with Voice and SMS capabilities</li>
                <li>Add credentials to backend/.env file</li>
              </ol>
            </div>
          </Card>
        </TabsContent>

        {/* Make.com Settings */}
        <TabsContent value="make">
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center">
                <Webhook className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-800">Make.com Automation</h3>
                <p className="text-sm text-slate-600">Workflow automation webhooks</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Webhook URL</label>
                <Input
                  data-testid="make-webhook-input"
                  value={configs.make.webhook_url}
                  onChange={(e) =>
                    setConfigs({
                      ...configs,
                      make: { webhook_url: e.target.value }
                    })
                  }
                  placeholder="https://hook.us2.make.com/..."
                />
              </div>
              <Button
                data-testid="save-make-button"
                onClick={() => handleSave('Make.com')}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Make.com Settings
              </Button>
            </div>

            <div className="mt-6 p-4 bg-indigo-50 rounded-lg">
              <p className="text-sm text-indigo-800 font-medium mb-2">Setup Instructions:</p>
              <ol className="text-sm text-indigo-700 space-y-1 list-decimal list-inside">
                <li>Create a new scenario in Make.com</li>
                <li>Add a Webhook trigger module</li>
                <li>Copy the webhook URL</li>
                <li>Paste it here and save to backend/.env</li>
              </ol>
            </div>
          </Card>
        </TabsContent>

        {/* FUB CRM Settings */}
        <TabsContent value="fub">
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center">
                <Database className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-800">Follow Up Boss CRM</h3>
                <p className="text-sm text-slate-600">Lead management integration</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">API Key</label>
                <Input
                  data-testid="fub-apikey-input"
                  type="password"
                  value={configs.fub.api_key}
                  onChange={(e) =>
                    setConfigs({
                      ...configs,
                      fub: { ...configs.fub, api_key: e.target.value }
                    })
                  }
                  placeholder="Your FUB API Key"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Base URL</label>
                <Input
                  data-testid="fub-url-input"
                  value={configs.fub.base_url}
                  onChange={(e) =>
                    setConfigs({
                      ...configs,
                      fub: { ...configs.fub, base_url: e.target.value }
                    })
                  }
                  placeholder="https://api.followupboss.com/v1"
                />
              </div>
              <Button
                data-testid="save-fub-button"
                onClick={() => handleSave('FUB CRM')}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600"
              >
                <Save className="w-4 h-4 mr-2" />
                Save FUB Settings
              </Button>
            </div>

            <div className="mt-6 p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-green-800 font-medium mb-2">API Key Instructions:</p>
              <ol className="text-sm text-green-700 space-y-1 list-decimal list-inside">
                <li>Log in to Follow Up Boss</li>
                <li>Go to Settings → API</li>
                <li>Generate a new API key</li>
                <li>Add to backend/.env as FUB_API_KEY</li>
              </ol>
            </div>
          </Card>
        </TabsContent>

        {/* Email Settings */}
        <TabsContent value="email">
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-800">Email Configuration</h3>
                <p className="text-sm text-slate-600">SMTP settings for email agent</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">SMTP Host</label>
                  <Input
                    data-testid="email-host-input"
                    value={configs.email.smtp_host}
                    onChange={(e) =>
                      setConfigs({
                        ...configs,
                        email: { ...configs.email, smtp_host: e.target.value }
                      })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">SMTP Port</label>
                  <Input
                    data-testid="email-port-input"
                    value={configs.email.smtp_port}
                    onChange={(e) =>
                      setConfigs({
                        ...configs,
                        email: { ...configs.email, smtp_port: e.target.value }
                      })
                    }
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Email Address</label>
                <Input
                  data-testid="email-user-input"
                  type="email"
                  value={configs.email.smtp_user}
                  onChange={(e) =>
                    setConfigs({
                      ...configs,
                      email: { ...configs.email, smtp_user: e.target.value }
                    })
                  }
                  placeholder="your-email@gmail.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">App Password</label>
                <Input
                  data-testid="email-password-input"
                  type="password"
                  value={configs.email.smtp_password}
                  onChange={(e) =>
                    setConfigs({
                      ...configs,
                      email: { ...configs.email, smtp_password: e.target.value }
                    })
                  }
                  placeholder="Gmail App Password"
                />
              </div>
              <Button
                data-testid="save-email-button"
                onClick={() => handleSave('Email')}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Email Settings
              </Button>
            </div>

            <div className="mt-6 p-4 bg-purple-50 rounded-lg">
              <p className="text-sm text-purple-800 font-medium mb-2">Gmail Setup:</p>
              <ol className="text-sm text-purple-700 space-y-1 list-decimal list-inside">
                <li>Enable 2-Step Verification in your Google Account</li>
                <li>Go to Security → App passwords</li>
                <li>Generate a new app password for 'Mail'</li>
                <li>Use that password here (not your Gmail password)</li>
              </ol>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Important Note */}
      <Card className="p-6 bg-yellow-50 border-yellow-200">
        <div className="flex items-start gap-3">
          <SettingsIcon className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
          <div>
            <h3 className="font-semibold text-yellow-900 mb-2">Important Configuration Note</h3>
            <p className="text-sm text-yellow-800">
              All credentials must be added to <code className="px-2 py-1 bg-yellow-100 rounded">/app/backend/.env</code> file for the integrations to work. The settings shown here are for reference. After updating the .env file, restart the backend server:
            </p>
            <code className="block mt-2 p-3 bg-yellow-100 rounded text-xs">
              sudo supervisorctl restart backend
            </code>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Settings;